import React, { useState, useEffect, useCallback } from 'react';
import {
  SearchIcon,
  ArrowDown,
  AccountIcon,
  DiscountIcon,
  RefreshIcon,
  PaymentsIcon,
  FilterIcon,
  CloseIcon
} from '../../../public/icons';
import AdminLayout from '../layout';
import Pagination from '../../../component/shared/Pagination';
import PaymentDetailsModal from '../../../component/admin/payment/PaymentDetailsModal';
import OrderDetailsModal from '../../../component/admin/orders/OrderDetailsModal';
import adminTransactionService from '../../../services/admin/admin-transactionService';
import adminOrderService from '../../../services/admin/admin-orderService';

const PaymentsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  const [stats, setStats] = useState({
    totalRevenue: 0,
    transactionsThisMonth: 0,
    successfulTransactions: 0,
    pendingRefunds: 0
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 20,
    totalPages: 1,
    totalItems: 0
  });

  // 🟢 Transaction modal state
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);

  // 🟢 Order modal state (opened from inside transaction modal)
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);

  /* ───────── Fetch transactions ───────── */
  const fetchTransactions = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const res = await adminTransactionService.getTransactions({
          page,
          limit: pagination.itemsPerPage,
          search: searchQuery || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined
        });

        if (res.success) {
          setTransactions(res.data.transactions || []);
          setPagination((p) => ({
            ...p,
            currentPage: res.data.pagination.page,
            totalPages: res.data.pagination.pages,
            totalItems: res.data.pagination.total
          }));
        } else {
          setTransactions([]);
        }
      } catch (err) {
        console.error('Fetch transactions error:', err);
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, statusFilter, pagination.itemsPerPage]
  );

  /* ───────── Fetch stats ───────── */
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const res = await adminTransactionService.getStats();
      if (res.success) setStats(res.data);
    } catch (err) {
      console.error('Fetch stats error:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions(pagination.currentPage);
  }, [fetchTransactions, pagination.currentPage]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (pagination.currentPage === 1) fetchTransactions(1);
      else setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery, statusFilter, fetchTransactions]);

  /* ───────── Handlers ───────── */
  const handleViewTransaction = (id) => {
    setSelectedTransactionId(id);
    setIsTransactionModalOpen(true);
  };

  const handleCloseTransactionModal = () => {
    setIsTransactionModalOpen(false);
    setSelectedTransactionId(null);
  };

  /**
   * 🟢 Called from inside PaymentDetailsModal when "View Order" is clicked.
   * Receives the orderId, fetches the full order, then opens OrderDetailsModal.
   */
  const handleViewOrderFromTransaction = async (orderId) => {
    if (!orderId) return;
    setOrderLoading(true);
    try {
      const res = await adminOrderService.getOrderById(orderId);
      if (res.success && res.data?.order) {
        setSelectedOrder(res.data.order);
        setIsOrderModalOpen(true);
      } else if (res.success && res.data) {
        // some versions of the endpoint return the order directly on data
        setSelectedOrder(res.data);
        setIsOrderModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to load order from transaction:', err);
    } finally {
      setOrderLoading(false);
    }
  };

  const handleCloseOrderModal = () => {
    setIsOrderModalOpen(false);
    setSelectedOrder(null);
  };

  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, currentPage: page }));
  };

  /* ───────── Stats cards ───────── */
  const statsCards = [
    {
      label: 'Total Revenue',
      value: `₦${(stats.totalRevenue || 0).toLocaleString()}`,
      icon: <PaymentsIcon />,
      color: 'bg-[#F4E7F1] text-[#8F2F84]'
    },
    {
      label: 'Transactions this month',
      value: stats.transactionsThisMonth,
      icon: <AccountIcon />,
      color: 'bg-[#E7ECF4] text-[#2F578F]'
    },
    {
      label: 'Successful',
      value: stats.successfulTransactions,
      icon: <DiscountIcon />,
      color: 'bg-[#F4F2E7] text-[#8F7F2F]'
    },
    {
      label: 'Pending Refund',
      value: stats.pendingRefunds,
      icon: <RefreshIcon />,
      color: 'bg-[#F4E7E7] text-[#A82121]'
    }
  ];

  const statusFilters = [
    { id: 'all', label: 'All status' },
    { id: 'success', label: 'Completed' },
    { id: 'pending', label: 'Pending' },
    { id: 'failed', label: 'Failed' },
    { id: 'abandoned', label: 'Abandoned' },
    { id: 'reversed', label: 'Reversed' }
  ];

  const getStatusBadge = (status) => {
    const map = {
      success: { color: 'bg-[#E7F4ED] text-[#2F8F5B]', label: 'Completed' },
      pending: { color: 'bg-[#FFF8E1] text-[#F57C00]', label: 'Pending' },
      failed: { color: 'bg-[#FFEBEE] text-[#D32F2F]', label: 'Failed' },
      abandoned: { color: 'bg-gray-100 text-gray-700', label: 'Abandoned' },
      reversed: { color: 'bg-[#F4E7E7] text-[#A82121]', label: 'Reversed' }
    };
    const cfg = map[status] || map.pending;
    return (
      <span
        className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${cfg.color}`}
      >
        <span className="mr-0.5 sm:mr-1 text-[8px] sm:text-xs">●</span>
        {cfg.label}
      </span>
    );
  };

  const formatNaira = (kobo) =>
    `₦${((Number(kobo) || 0) / 100).toLocaleString()}`;

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      : '';

  /* ───────── Mobile Filters ───────── */
  const MobileFiltersModal = () => (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={() => setMobileFiltersOpen(false)}
      />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-medium text-[#1C2A46]">Filters</h3>
          <button onClick={() => setMobileFiltersOpen(false)} className="p-1">
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search by reference or customer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#D4A574] text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <div className="grid grid-cols-2 gap-2">
              {statusFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => {
                    setStatusFilter(filter.id);
                    setMobileFiltersOpen(false);
                  }}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    statusFilter === filter.id
                      ? 'bg-[#D2A146] text-white border-[#D2A146]'
                      : 'bg-white text-[#1C2A46] border-[#DBDBDB] hover:border-[#D2A146]'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
              setMobileFiltersOpen(false);
            }}
            className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded hover:bg-gray-50 transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={() => setMobileFiltersOpen(false)}
            className="flex-1 py-3 bg-[#D2A146] text-white font-medium rounded hover:bg-[#C49660] transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );

  /* ───────── Render ───────── */
  return (
    <AdminLayout>
      <div className="p-3 sm:p-4 md:p-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
          {statsCards.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-sm p-3 sm:p-4 md:p-5 shadow-sm"
            >
              <div className="text-left">
                <div
                  className={`flex justify-center items-center rounded-md h-10 w-10 sm:h-12 sm:w-12 ${stat.color}`}
                >
                  {stat.icon}
                </div>
                <div>
                  <p className="text-xl sm:text-2xl md:text-3xl font-medium text-[#0E1628] mt-2 sm:mt-4">
                    {statsLoading ? (
                      <span className="inline-block w-6 h-6 bg-gray-200 rounded animate-pulse" />
                    ) : (
                      stat.value
                    )}
                  </p>
                  <p className="text-xs sm:text-sm text-[#6B635D] mt-0.5 sm:mt-1">
                    {stat.label}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-sm shadow-sm">
          {/* Header */}
          <div className="mb-4 sm:mb-6 p-4 sm:p-6 border-b border-[#DBDBDB]">
            <div className="hidden md:flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">
                Recent Transactions
              </h2>

              <div className="flex gap-3 lg:gap-4 items-center">
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <SearchIcon size={18} className="text-[#6B635D]" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search reference, customer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="p-2 pl-9 pr-3 border w-64 lg:w-80 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs lg:placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                  />
                </div>

                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="p-2 pl-3 pr-7 border w-36 lg:w-40 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                  >
                    {statusFilters.map((filter) => (
                      <option key={filter.id} value={filter.id}>
                        {filter.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ArrowDown size={14} className="text-[#6B635D]" />
                  </div>
                </div>

                <button className="px-3 lg:px-4 py-2 border border-[#DBDBDB] text-[#0E1628] text-xs lg:text-sm font-medium hover:bg-gray-50 whitespace-nowrap">
                  Export CSV
                </button>
              </div>
            </div>

            <div className="md:hidden">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-medium text-[#0E1628]">
                  Recent Transactions
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setMobileFiltersOpen(true)}
                    className="flex items-center gap-2 px-3 py-2 border border-[#DBDBDB] rounded-sm text-sm"
                  >
                    <FilterIcon size={14} />
                    Filters
                  </button>
                  <button className="px-3 py-2 border border-[#DBDBDB] text-[#0E1628] text-sm font-medium hover:bg-gray-50 rounded-sm">
                    Export
                  </button>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <SearchIcon size={16} className="text-[#6B635D]" />
                </div>
                <input
                  type="text"
                  placeholder="Search reference, customer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full p-2.5 pl-10 pr-3 border border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-[650px] md:min-w-full divide-y divide-[#DBDBDB]">
              <thead className="bg-[#F3F4F6]">
                <tr>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Reference
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#DBDBDB] text-left">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D2A146] mx-auto" />
                      <p className="mt-3 text-sm text-gray-600">
                        Loading transactions…
                      </p>
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center py-12 text-sm text-gray-500"
                    >
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  transactions.map((txn) => (
                    <tr
                      key={txn._id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-medium text-[#0E1628]">
                          {txn.reference?.slice(0, 18) || '—'}
                          {txn.reference?.length > 18 ? '…' : ''}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm text-[#0E1628]">
                          {txn.orderNumber || txn.order?.orderNumber || '—'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm text-[#0E1628]">
                          {txn.customerName ||
                            txn.customerEmail ||
                            txn.user?.email ||
                            '—'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-medium text-[#0E1628]">
                          {formatNaira(txn.amount)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm text-[#8A9099]">
                          {formatDate(txn.createdAt)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        {getStatusBadge(txn.status)}
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewTransaction(txn._id)}
                          className="text-xs sm:text-sm font-normal text-secondary hover:text-[#C49660] transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && !loading && (
            <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-t border-[#DBDBDB]">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs sm:text-sm text-[#8A9099] order-2 sm:order-1">
                  Showing {transactions.length} out of{' '}
                  <span className="font-medium">{pagination.totalItems}</span>{' '}
                  transactions
                </div>
                <div className="order-1 sm:order-2 w-full sm:w-auto">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    itemsPerPage={pagination.itemsPerPage}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 🟢 Transaction Details Modal */}
      <PaymentDetailsModal
        isOpen={isTransactionModalOpen}
        onClose={handleCloseTransactionModal}
        transactionId={selectedTransactionId}
        onViewOrder={handleViewOrderFromTransaction}
      />

      {/* 🟢 Order Details Modal — opens from inside the transaction modal */}
      {orderLoading && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white px-6 py-4 rounded shadow-lg">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D2A146] mx-auto" />
            <p className="mt-2 text-xs text-gray-600">Loading order…</p>
          </div>
        </div>
      )}

      <OrderDetailsModal
        isOpen={isOrderModalOpen}
        onClose={handleCloseOrderModal}
        order={selectedOrder}
      />

      {/* Mobile Filters */}
      {mobileFiltersOpen && <MobileFiltersModal />}
    </AdminLayout>
  );
};

export default PaymentsPage;